// src/components/album/AlbumStoryBuilder.jsx
import React, { useState, useRef, useEffect } from 'react';
import { 
  Play, Pause, SkipForward, SkipBack, ChevronRight, Sidebar, 
  Music, Mic, Settings, Save, Grid, BookOpen,
  Move, Trash2, Plus, X, Loader2,
  Volume2, VolumeX, Edit
} from 'lucide-react';
import storyService from '../../services/storyService';
import { FileUpload } from '../common/FileUpload';

const AlbumStoryBuilder = ({ 
  user,
  albumId, 
  galleries,
  mediaItems, 
  onSave, 
  onClose,
  existingStory = null,
  isOwner = true 
}) => {
  const [storyData, setStoryData] = useState({
    title: '',
    description: '',
    mediaSequence: [],
    transitions: {},
    backgroundMusic: null,
    voiceover: null,
    duration: 5000, // Default 5 seconds per slide
    autoPlay: true,
    isPublic: true,

    reactionCounts: {
      like: 0,
      comment: 0,
      audio: 0,
      video: 0,
      photo: 0,
      total: 0
    }
  });
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isRecordingVoiceover, setIsRecordingVoiceover] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showMusicUpload, setShowMusicUpload] = useState(false);
  const [displayMode, setDisplayMode] = useState('cards'); // 'cards', 'carousel', 'sidebar'

  const [storyLevel, setStoryLevel] = useState('gallery');
  const [selectedGalleryId, setSelectedGalleryId] = useState(null);

  const [slideDurations, setSlideDurations] = useState({}); 
  const [defaultDuration, setDefaultDuration] = useState(5);
  
  const audioRecorderRef = useRef(null);
  const storyPlayerRef = useRef(null);
  const playIntervalRef = useRef(null);

  // Load existing story
  useEffect(() => {
    if (existingStory) {
      setStoryData({
        title: existingStory.title || '',
        description: existingStory.description || '',
        mediaSequence: existingStory.mediaSequence || [],
        transitions: existingStory.transitions || {},
        backgroundMusic: existingStory.backgroundMusic || null,
        voiceover: existingStory.voiceover || null,
        duration: existingStory.duration || 5000,
        autoPlay: existingStory.autoPlay !== false,
        isPublic: existingStory.isPublic !== false,
        reactionCounts: existingStory.reactionCounts || {
          like: 0,
          comment: 0,
          audio: 0,
          video: 0,
          photo: 0,
          total: 0
        }
      });
      
      // Set level and gallery from existing story
      setStoryLevel(existingStory.level || 'gallery');
      setSelectedGalleryId(existingStory.galleryId || galleries?.[0]?.id || null);
    } else {
      // Default for new stories
      setStoryLevel('gallery');
      setSelectedGalleryId(galleries?.[0]?.id || null);
    }
  }, [existingStory, galleries]);

  const toggleMediaInSequence = (mediaId) => {
    setStoryData(prev => {
      const sequence = [...prev.mediaSequence];
      const index = sequence.indexOf(mediaId);
      
      if (index > -1) {
        sequence.splice(index, 1);
      } else {
        sequence.push(mediaId);
      }
      
      return { ...prev, mediaSequence: sequence };
    });
  };

  const removeFromSequence = (mediaId) => {
    setStoryData(prev => ({
      ...prev,
      mediaSequence: prev.mediaSequence.filter(id => id !== mediaId)
    }));
  };

  const reorderSequence = (fromIndex, toIndex) => {
    setStoryData(prev => {
      const sequence = [...prev.mediaSequence];
      const [removed] = sequence.splice(fromIndex, 1);
      sequence.splice(toIndex, 0, removed);
      return { ...prev, mediaSequence: sequence };
    });
  };

  // Story Playback Controls
  const togglePlayback = () => {
    if (isPlaying) {
      setIsPlaying(false);
      if (playIntervalRef.current) {
        clearInterval(playIntervalRef.current);
      }
    } else {
      setIsPlaying(true);
      playIntervalRef.current = setInterval(() => {
        setCurrentSlide(prev => {
          const next = prev + 1;
          if (next >= storyData.mediaSequence.length) {
            setIsPlaying(false);
            clearInterval(playIntervalRef.current);
            return 0;
          }
          return next;
        });
      }, storyData.duration);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(Math.max(0, Math.min(index, storyData.mediaSequence.length - 1)));
  };

  // Voiceover Recording
  const toggleVoiceoverRecording = async () => {
    if (!isRecordingVoiceover) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        audioRecorderRef.current = new MediaRecorder(stream);
        
        const chunks = [];
        audioRecorderRef.current.ondataavailable = (e) => chunks.push(e.data);
        audioRecorderRef.current.onstop = () => handleVoiceoverComplete(chunks, stream);
        
        audioRecorderRef.current.start();
        setIsRecordingVoiceover(true);
      } catch (error) {
        console.error('Error accessing microphone:', error);
        alert('Could not access microphone');
      }
    } else {
      audioRecorderRef.current.stop();
      setIsRecordingVoiceover(false);
    }
  };

  const handleVoiceoverComplete = async (chunks, stream) => {
    const blob = new Blob(chunks, { type: 'audio/webm' });
    stream.getTracks().forEach(track => track.stop());
    
    try {
      const audioFile = new File([blob], `voiceover-${Date.now()}.webm`, {
        type: 'audio/webm'
      });
      
      const uploadResult = await storyService.uploadStoryAssets(albumId, audioFile, 'voiceovers');
      
      setStoryData(prev => ({
        ...prev,
        voiceover: {
          url: uploadResult.url,
          fileName: uploadResult.fileName,
          duration: Math.round(blob.size / 16000)
        }
      }));
      
    } catch (error) {
      console.error('Error uploading voiceover:', error);
      alert('Failed to save voiceover');
    }
  };

  // Background Music Upload
  const handleMusicUpload = async (files, error) => {
    if (error) {
      alert(`Error: ${error}`);
      return;
    }

    const file = Array.isArray(files) ? files[0] : files;
    
    if (!file.type.startsWith('audio/')) {
      alert('Please select an audio file');
      return;
    }

    try {
      const uploadResult = await storyService.uploadStoryAssets(albumId, file, 'music');
      
      setStoryData(prev => ({
        ...prev,
        backgroundMusic: {
          url: uploadResult.url,
          fileName: uploadResult.fileName || file.name,
          originalName: file.name
        }
      }));
      
      setShowMusicUpload(false);
    } catch (error) {
      console.error('Error uploading music:', error);
      alert('Failed to upload background music');
    }
  };

  // Save Story
  const handleSave = async () => {
    if (!storyData.title.trim()) {
      alert('Please enter a story title');
      return;
    }
    
    if (storyData.mediaSequence.length === 0) {
      alert('Please select at least one photo for your story');
      return;
    }

    // Validate gallery selection for gallery-level stories
    if (storyLevel === 'gallery' && !selectedGalleryId) {
      alert('Please select a gallery for this story');
      return;
    }

    setSaving(true);
    
    try {
      // Prepare story data with level and gallery information
      const finalStoryData = {
        ...storyData,
        level: storyLevel,
        galleryId: storyLevel === 'gallery' ? selectedGalleryId : null,
        albumId: albumId,
        displayMode: displayMode,  // NEW: Include display mode
        defaultDuration: defaultDuration * 1000,
        slideDurations: Object.fromEntries(
          Object.entries(slideDurations).map(([id, seconds]) => [id, seconds * 1000])
        ),
        createdBy: user?.uid
      };

      if (existingStory) {
        await storyService.updateStory(albumId, existingStory.id, finalStoryData);
      } else {
        await storyService.createStory(albumId, finalStoryData);
      }
      
      if (onSave) {
        onSave(finalStoryData);
      }
      
      alert('Story saved successfully!');
      
    } catch (error) {
      console.error('Error saving story:', error);
      alert('Failed to save story');
    } finally {
      setSaving(false);
    }
  };

  const DisplayModeSelection = () => (
    <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
      <h3 className="text-lg font-semibold">Story Display Mode</h3>
      <p className="text-sm text-gray-600 mb-4">
        Choose how this story appears in the {storyLevel === 'album' ? 'album' : 'gallery'}
      </p>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { 
            id: 'cards', 
            label: 'Story Card', 
            desc: 'Appears as a card mixed with media',
            icon: Grid,
            preview: '🎴'
          },
          { 
            id: 'carousel', 
            label: 'Top Carousel', 
            desc: 'Horizontal scrollable at top',
            icon: ChevronRight,
            preview: '📽️'
          },
          { 
            id: 'sidebar', 
            label: 'Side Panel', 
            desc: 'Collapsible sidebar overlay',
            icon: Sidebar,
            preview: '📋'
          }
        ].map(mode => {
          const IconComponent = mode.icon;
          return (
            <button
              key={mode.id}
              type="button"
              onClick={() => setDisplayMode(mode.id)}
              className={`p-4 border-2 rounded-lg text-left transition-colors ${
                displayMode === mode.id
                  ? 'border-green-500 bg-green-50 text-green-700'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <IconComponent className="h-5 w-5" />
                <span className="text-lg">{mode.preview}</span>
              </div>
              <div className="font-medium">{mode.label}</div>
              <div className="text-sm text-gray-500">{mode.desc}</div>
            </button>
          );
        })}
      </div>

      {/* Display Mode Specific Settings */}
      {displayMode === 'sidebar' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <h4 className="font-medium text-blue-900 mb-2">Sidebar Settings</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-blue-800 mb-1">
                Sidebar Position
              </label>
              <select
                value={storyData.sidebarPosition || 'right'}
                onChange={(e) => setStoryData(prev => ({
                  ...prev,
                  sidebarPosition: e.target.value
                }))}
                className="w-full px-2 py-1 border border-blue-300 rounded text-sm"
              >
                <option value="left">Left Side</option>
                <option value="right">Right Side</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-blue-800 mb-1">
                Sidebar Width
              </label>
              <select
                value={storyData.sidebarWidth || 'normal'}
                onChange={(e) => setStoryData(prev => ({
                  ...prev,
                  sidebarWidth: e.target.value
                }))}
                className="w-full px-2 py-1 border border-blue-300 rounded text-sm"
              >
                <option value="narrow">Narrow (300px)</option>
                <option value="normal">Normal (400px)</option>
                <option value="wide">Wide (500px)</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {displayMode === 'carousel' && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
          <h4 className="font-medium text-purple-900 mb-2">Carousel Settings</h4>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={storyData.carouselAutoPlay || false}
              onChange={(e) => setStoryData(prev => ({
                ...prev,
                carouselAutoPlay: e.target.checked
              }))}
              className="rounded border-purple-300 text-purple-600"
            />
            <span className="text-sm text-purple-800">Auto-play carousel</span>
          </label>
        </div>
      )}
    </div>
  );

  const StorySequenceEditor = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Story Sequence</h3>
      
      <div className="grid grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg max-h-60 overflow-y-auto">
        {mediaItems.map(item => (
          <div 
            key={item.id}
            className={`relative cursor-pointer rounded-lg overflow-hidden transition-transform hover:scale-105 ${
              storyData.mediaSequence.includes(item.id) ? 'ring-2 ring-blue-500' : ''
            }`}
            onClick={() => toggleMediaInSequence(item.id)}
          >
            <img 
              src={item.thumbnailUrl || item.url} 
              alt={item.title}
              className="w-full h-20 object-cover"
            />
            {storyData.mediaSequence.includes(item.id) && (
              <div className="absolute top-1 right-1 bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-semibold">
                {storyData.mediaSequence.indexOf(item.id) + 1}
              </div>
            )}
          </div>
        ))}
      </div>
      
      {/* Selected Sequence */}
      {storyData.mediaSequence.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-medium">Selected Sequence ({storyData.mediaSequence.length} items)</h4>
          <div className="flex flex-wrap gap-2">
            {storyData.mediaSequence.map((mediaId, index) => {
              const item = mediaItems.find(m => m.id === mediaId);
              return (
                <div key={mediaId} className="flex items-center gap-1 bg-blue-100 rounded-full px-2 py-1">
                  <span className="text-xs font-medium">{index + 1}</span>
                  <img src={item?.thumbnailUrl} alt="" className="w-6 h-6 rounded object-cover" />
                  <span className="text-xs truncate max-w-16">{item?.title}</span>
                  <button
                    onClick={() => removeFromSequence(mediaId)}
                    className="text-gray-500 hover:text-red-500 transition-colors"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  const StoryPreview = () => {
    const currentMediaId = storyData.mediaSequence[currentSlide];
    const currentMedia = mediaItems.find(m => m.id === currentMediaId);
    
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Preview</h3>
        {storyData.mediaSequence.length > 0 ? (
          <div className="aspect-video bg-gray-900 rounded-lg overflow-hidden relative">
            {currentMedia && (
              <img 
                src={currentMedia.url} 
                alt={currentMedia.title}
                className="w-full h-full object-cover"
              />
            )}
            
            {/* Story overlay info */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
              <h4 className="text-white font-semibold">{currentMedia?.title}</h4>
              <p className="text-gray-300 text-sm">{currentMedia?.description}</p>
            </div>
            
            {/* Slide progress */}
            <div className="absolute top-4 left-4 right-4">
              <div className="flex space-x-1">
                {storyData.mediaSequence.map((_, index) => (
                  <div
                    key={index}
                    className={`h-1 flex-1 rounded ${
                      index === currentSlide ? 'bg-white' : 'bg-white bg-opacity-50'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
            <div className="text-center text-gray-500">
              <Plus className="h-8 w-8 mx-auto mb-2" />
              <p>Select photos to create your story</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  const StoryControls = () => (
    <div className="flex items-center gap-4 p-4 bg-white rounded-lg border">
      <button
        onClick={togglePlayback}
        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
      >
        {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
        {isPlaying ? 'Pause' : 'Preview'}
      </button>
      
      <div className="flex items-center gap-2">
        <button onClick={() => goToSlide(currentSlide - 1)} disabled={currentSlide === 0}>
          <SkipBack className="h-4 w-4" />
        </button>
        <span className="text-sm text-gray-600">
          {currentSlide + 1} of {storyData.mediaSequence.length}
        </span>
        <button onClick={() => goToSlide(currentSlide + 1)} disabled={currentSlide >= storyData.mediaSequence.length - 1}>
          <SkipForward className="h-4 w-4" />
        </button>
      </div>

      <button
        onClick={toggleVoiceoverRecording}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
          isRecordingVoiceover 
            ? 'bg-red-100 border-red-300 text-red-700' 
            : 'hover:bg-gray-50'
        }`}
      >
        <Mic className="h-4 w-4" />
        {isRecordingVoiceover ? 'Stop Recording' : 'Add Voiceover'}
      </button>

      <button 
        onClick={() => setShowMusicUpload(true)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg border hover:bg-gray-50"
      >
        <Music className="h-4 w-4" />
        Background Music
      </button>

      <button
        onClick={handleSave}
        disabled={saving}
        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors ml-auto"
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        {saving ? 'Saving...' : 'Save Story'}
      </button>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold">
            {existingStory ? 'Edit Story' : 'Create Story'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Story Title</label>
                <input
                  type="text"
                  value={storyData.title}
                  onChange={(e) => setStoryData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter story title..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={storyData.description}
                  onChange={(e) => setStoryData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows={3}
                  placeholder="Tell the story behind your photos..."
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Story Type
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setStoryLevel('gallery')}
                    className={`p-4 border-2 rounded-lg ${
                      storyLevel === 'gallery' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-300'
                    }`}
                  >
                    <Grid className="h-6 w-6 mx-auto mb-2" />
                    <div className="font-medium">Gallery Story</div>
                    <div className="text-sm text-gray-500">Tied to specific gallery</div>
                  </button>
                  
                  <button
                    onClick={() => setStoryLevel('album')}
                    className={`p-4 border-2 rounded-lg ${
                      storyLevel === 'album' 
                        ? 'border-purple-500 bg-purple-50' 
                        : 'border-gray-300'
                    }`}
                  >
                    <BookOpen className="h-6 w-6 mx-auto mb-2" />
                    <div className="font-medium">Album Story</div>
                    <div className="text-sm text-gray-500">Standalone page</div>
                  </button>
                </div>
              </div>

              <DisplayModeSelection/>

              <StorySequenceEditor />
              {/* Gallery selection for gallery-level stories */}
              {storyLevel === 'gallery' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Gallery
                  </label>
                  <select
                    value={selectedGalleryId || ''}
                    onChange={(e) => setSelectedGalleryId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">Choose a gallery...</option>
                    {galleries.map(gallery => (
                      <option key={gallery.id} value={gallery.id}>
                        {gallery.name} ({gallery.mediaItems?.length || 0} items)
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            
            
            <div className="space-y-4">
              <StoryPreview />
              
              {/* Story Settings */}
              <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                <h4 className="font-medium">Story Settings</h4>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm text-gray-700">Slide Duration</label>
                  <select
                    value={storyData.duration}
                    onChange={(e) => setStoryData(prev => ({ ...prev, duration: Number(e.target.value) }))}
                    className="px-2 py-1 border rounded text-sm"
                  >
                    <option value={3000}>3 seconds</option>
                    <option value={5000}>5 seconds</option>
                    <option value={7000}>7 seconds</option>
                    <option value={10000}>10 seconds</option>
                  </select>
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm text-gray-700">Auto-play</label>
                  <input
                    type="checkbox"
                    checked={storyData.autoPlay}
                    onChange={(e) => setStoryData(prev => ({ ...prev, autoPlay: e.target.checked }))}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm text-gray-700">Public Story</label>
                  <input
                    type="checkbox"
                    checked={storyData.isPublic}
                    onChange={(e) => setStoryData(prev => ({ ...prev, isPublic: e.target.checked }))}
                    className="rounded"
                  />
                </div>

                {/* Audio Status */}
                {storyData.voiceover && (
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <Volume2 className="h-4 w-4" />
                    Voiceover added
                  </div>
                )}
                
                {storyData.backgroundMusic && (
                  <div className="flex items-center gap-2 text-sm text-blue-600">
                    <Music className="h-4 w-4" />
                    Music: {storyData.backgroundMusic.originalName}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Footer Controls */}
        <div className="p-6 border-t">
          <StoryControls />
        </div>
        
        {/* Music Upload Modal */}
        {showMusicUpload && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-60 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Add Background Music</h3>
                <button
                  onClick={() => setShowMusicUpload(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <FileUpload
                accept="audio/*"
                maxFiles={1}
                maxSize={20 * 1024 * 1024} // 20MB
                onFilesSelect={handleMusicUpload}
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AlbumStoryBuilder;